import React, { useRef, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { LayoutDashboardIcon, FolderIcon, UsersIcon, GlobeIcon, MapPinIcon, CpuIcon, LeafIcon, BarChart3Icon, FileTextIcon, SettingsIcon, ActivityIcon, CheckCircle2Icon, ShieldCheckIcon, ClockIcon, ZapIcon } from 'lucide-react';
type IconComponent = ComponentType<{
  className?: string;
}>;
const sidebarIconMap: Record<string, IconComponent> = {
  Dashboard: LayoutDashboardIcon,
  Projects: FolderIcon,
  Sites: FolderIcon,
  Farmers: UsersIcon,
  Satellite: GlobeIcon,
  'Field Data': MapPinIcon,
  'Soil Data': MapPinIcon,
  Meters: CpuIcon,
  Practices: LeafIcon,
  Baselines: BarChart3Icon,
  Reports: FileTextIcon,
  Settings: SettingsIcon
};
const activityIconMap: Record<string, IconComponent> = {
  ActivityIcon,
  CheckCircle2Icon,
  ShieldCheckIcon,
  GlobeIcon,
  MapPinIcon,
  CpuIcon,
  ZapIcon,
  BarChart3Icon,
  UsersIcon,
  LeafIcon
};
interface SidebarItem {
  icon: string;
  label: string;
  active?: boolean;
}
interface StatCard {
  label: string;
  value: string;
  highlight?: boolean;
}
interface ActivityItem {
  icon: string;
  iconColor: string;
  label: string;
  detail: string;
  time: string;
}
export interface PlatformPreviewProps {
  headline: string;
  highlightWord: string;
  subtext: string;
  urlPath: string;
  projectName: string;
  statusLabel: string;
  sidebarItems: SidebarItem[];
  stats: StatCard[];
  chartLabel: string;
  chartSubLabel?: string;
  activityItems: ActivityItem[];
}
function DashboardMockup({
  urlPath,
  projectName,
  statusLabel,
  sidebarItems,
  stats,
  chartLabel,
  activityItems
}: Omit<PlatformPreviewProps, 'headline' | 'highlightWord' | 'subtext' | 'chartSubLabel'>) {
  const barHeights = [55, 78, 42, 88, 65, 50, 82];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return <div className="rounded-2xl shadow-2xl border border-carbon-geo overflow-hidden bg-white" style={{
    transform: 'perspective(1200px) rotateX(2deg) rotateY(-1deg) scale(0.9)'
  }} data-id="element-711">
      {/* Browser Chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200" data-id="element-712">
        <div className="flex gap-1.5" data-id="element-713">
          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-714" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-715" />
          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-716" />
        </div>
        <div className="flex-1 mx-4" data-id="element-717">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono border border-gray-200 max-w-md" data-id="element-718">
            app.carbonsafe.io/{urlPath}
          </div>
        </div>
      </div>

      <div className="flex min-h-[400px]" data-id="element-719">
        {/* Sidebar */}
        <div className="w-44 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-720">
          <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-721">
            <div className="w-7 h-7 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-722">
              <span className="text-white text-xs font-bold" data-id="element-723">CS</span>
            </div>
            <span className="text-white text-sm font-bold" data-id="element-724">CarbonSafe</span>
          </div>
          <nav className="space-y-0.5" data-id="element-725">
            {sidebarItems.map(item => {
            const SidebarIcon = sidebarIconMap[item.label] || FolderIcon;
            return <div key={item.label} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs ${item.active ? 'bg-white/10 text-white font-medium' : 'text-white/50'}`} data-id="element-726">
                  <SidebarIcon className="w-3.5 h-3.5" data-id="element-727" />
                  {item.label}
                </div>;
          })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-5 bg-gray-50 overflow-hidden" data-id="element-728">
          {/* Header */}
          <div className="flex items-center justify-between mb-4" data-id="element-729">
            <div data-id="element-730">
              <h3 className="text-sm font-bold text-carbon-deep" data-id="element-731">
                {projectName}
              </h3>
              <p className="text-[11px] text-gray-400 mt-0.5" data-id="element-732">
                Last updated 15 min ago
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-id="element-733">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-734" />
              {statusLabel}
            </span>
          </div>

          {/* Mini Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4" data-id="element-735">
            {stats.map(stat => <div key={stat.label} className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-736">
                <p className="text-[10px] text-gray-400 font-medium" data-id="element-737">
                  {stat.label}
                </p>
                <p className={`text-lg font-bold mt-0.5 ${stat.highlight ? 'text-carbon-bright' : 'text-carbon-deep'}`} data-id="element-738">
                  {stat.value}
                </p>
              </div>)}
          </div>

          {/* Chart + Map row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 mb-4" data-id="element-739">
            {/* Bar Chart */}
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-740">
              <div className="flex items-center justify-between mb-3" data-id="element-741">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-742">
                  {chartLabel}
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-743">This week</p>
              </div>
              <div className="flex items-end gap-2 h-24" data-id="element-744">
                {barHeights.map((h, i) => <div key={i} className="flex-1 flex flex-col items-center gap-1" data-id="element-745">
                    <div className="w-full rounded-t-sm bg-carbon-bright/80" style={{
                  height: `${h}%`
                }} data-id="element-746" />
                    <span className="text-[9px] text-gray-400" data-id="element-747">{days[i]}</span>
                  </div>)}
              </div>
            </div>

            {/* Map/Coverage placeholder */}
            <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-748">
              <div className="flex items-center justify-between mb-3" data-id="element-749">
                <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-750">
                  Coverage Map
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-751">All sites</p>
              </div>
              <div className="relative h-24 rounded-lg bg-emerald-50 overflow-hidden" data-id="element-752">
                <div className="absolute inset-0 opacity-20" data-id="element-753">
                  {[...Array(6)].map((_, i) => <div key={`v-${i}`} className="absolute border-r border-emerald-300" style={{
                  left: `${i * 20}%`,
                  top: 0,
                  width: '1px',
                  height: '100%'
                }} data-id="element-754" />)}
                  {[...Array(4)].map((_, i) => <div key={`h-${i}`} className="absolute border-b border-emerald-300" style={{
                  top: `${i * 33}%`,
                  left: 0,
                  width: '100%',
                  height: '1px'
                }} data-id="element-755" />)}
                </div>
                <div className="absolute w-3 h-3 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '22%',
                top: '28%'
              }} data-id="element-756" />
                <div className="absolute w-2.5 h-2.5 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '48%',
                top: '52%'
              }} data-id="element-757" />
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright/80 shadow-sm" style={{
                left: '68%',
                top: '22%'
              }} data-id="element-758" />
                <div className="absolute w-2.5 h-2.5 rounded-full bg-carbon-bright shadow-sm shadow-carbon-bright/30" style={{
                left: '35%',
                top: '65%'
              }} data-id="element-759" />
                <div className="absolute w-2 h-2 rounded-full bg-carbon-bright/70 shadow-sm" style={{
                left: '78%',
                top: '55%'
              }} data-id="element-760" />
                <div className="absolute w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm" style={{
                left: '55%',
                top: '38%'
              }} data-id="element-761" />
              </div>
            </div>
          </div>

          {/* Activity Table */}
          <div className="bg-white rounded-lg border border-gray-100 overflow-hidden" data-id="element-762">
            <div className="px-3 py-2 border-b border-gray-100" data-id="element-763">
              <p className="text-[11px] font-semibold text-carbon-deep" data-id="element-764">
                Recent Activity
              </p>
            </div>
            <div className="divide-y divide-gray-50" data-id="element-765">
              {activityItems.map((item, i) => {
              const ActivityIcon = activityIconMap[item.icon] || ActivityIcon;
              return <div key={i} className="flex items-center gap-3 px-3 py-2" data-id="element-766">
                    <ActivityIcon className={`w-3 h-3 flex-shrink-0 ${item.iconColor}`} data-id="element-767" />
                    <span className="text-[11px] text-carbon-deep font-medium flex-1 truncate" data-id="element-768">
                      {item.label}
                    </span>
                    <span className="text-[10px] text-gray-500 hidden sm:inline" data-id="element-769">
                      {item.detail}
                    </span>
                    <span className="text-[10px] text-gray-400 flex items-center gap-1 flex-shrink-0" data-id="element-770">
                      <ClockIcon className="w-2.5 h-2.5" data-id="element-771" />
                      {item.time}
                    </span>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function PlatformPreview(props: PlatformPreviewProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section ref={ref} className="w-full bg-[#FAFDF7] py-20 sm:py-28" data-id="element-772">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-773">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-774">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-carbon-bright bg-carbon-bright/10 px-3 py-1 rounded-full mb-5" data-id="element-775">
            Platform Preview
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-carbon-deep tracking-tight" data-id="element-776">
            {props.headline}{' '}
            <span className="text-carbon-bright" data-id="element-777">{props.highlightWord}</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-778">
            {props.subtext}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 60
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-779">
          <DashboardMockup urlPath={props.urlPath} projectName={props.projectName} statusLabel={props.statusLabel} sidebarItems={props.sidebarItems} stats={props.stats} chartLabel={props.chartLabel} activityItems={props.activityItems} data-id="element-780" />
        </motion.div>
      </div>
    </section>;
}